import React, { useCallback } from 'react'

import Link from 'next/link'
import { HiOutlineArrowRight } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import DemoTooltip from '@/components/billing/DemoTooltip'
import { MiniBadge } from '@/components/common/Badge'
import { isActive } from '@/components/common/Sidebar/lib'
import { Skeleton } from '@/components/common/Skeleton'

import { DEMO_URL } from '@/constants/general'

import { usePathname } from '@/hooks/common/usePathname'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import {
  useSidebarHoverSuppressed,
  useSidebarSetHoverSuppressed,
} from '@/stores/SidebarStore'

import type { NavigationId, NavigationOption } from '@/types/navigation/Sidebar'

interface FlyoutSectionLabelProps {
  children: React.ReactNode
  className?: string
}

export const FlyoutSectionLabel: React.FC<FlyoutSectionLabelProps> = ({
  children,
  className,
}) => (
  <div
    className={twMerge(
      'px-2 mb-2 text-primary-600 dark:text-primary-300',
      className,
    )}
  >
    <span className="text-xs font-semibold uppercase tracking-wider">
      {children}
    </span>
  </div>
)

interface FlyoutSectionProps {
  children: React.ReactNode
  hasBorderTop?: boolean
  className?: string
}

export const FlyoutSection: React.FC<FlyoutSectionProps> = ({
  children,
  hasBorderTop = false,
  className,
}) => (
  <div
    className={twMerge(
      'py-2 px-2',
      hasBorderTop && 'border-t border-primary-100 dark:border-primary-800',
      className,
    )}
  >
    {children}
  </div>
)

export const FlyoutFooter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="border-t border-primary-100 dark:border-primary-800 px-4 py-3">
    {children}
  </div>
)

interface FlyoutListItemProps {
  href: string
  isActive: boolean
  children: React.ReactNode
  isMuted?: boolean
  onClick?: () => void
}

export const FlyoutListItem: React.FC<FlyoutListItemProps> = ({
  href,
  isActive: active,
  children,
  isMuted = false,
  onClick,
}) => (
  <li className="w-full">
    <Link
      href={href}
      onClick={onClick}
      className={twMerge(
        'flex items-center w-full px-2 py-1.5 rounded-md',
        'transition-colors duration-100',
        active
          ? 'bg-blue-50 dark:bg-blue-500/10'
          : 'hover:bg-primary-100 dark:hover:bg-primary-800',
      )}
    >
      <div
        className={twMerge(
          'truncate text-sm w-full',
          active
            ? 'text-blue-700 dark:text-blue-300 font-medium'
            : isMuted
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-primary-800 dark:text-primary-200',
        )}
      >
        {children}
      </div>
    </Link>
  </li>
)

export const FlyoutList: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <ul className="flex flex-col gap-0.5">{children}</ul>

export const LoadingSkeletons: React.FC<{ count?: number }> = ({
  count = 5,
}) => (
  <div className="flex flex-col gap-1.5 px-2 mb-1">
    {[...Array(count)].map((_, i) => (
      <Skeleton
        key={i}
        className="h-7 w-full bg-primary-100 dark:bg-primary-800"
      />
    ))}
  </div>
)

interface EmptyStateProps {
  children: React.ReactNode
  className?: string
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  children,
  className,
}) => (
  <p
    className={twMerge(
      'px-2 pb-1 text-sm text-primary-600 dark:text-primary-400',
      className,
    )}
  >
    {children}
  </p>
)

interface StatCardProps {
  href: string
  label: string
  description?: string
  value: React.ReactNode
  isLoading?: boolean
  variant?: 'default' | 'large' | 'alert'
  skeletonWidth?: string
}

export const StatCard: React.FC<StatCardProps> = ({
  href,
  label,
  description,
  value,
  isLoading,
  variant = 'default',
  skeletonWidth = 'w-8',
}) => {
  const isLarge = variant === 'large'
  const isAlert = variant === 'alert'

  return (
    <Link
      href={href}
      className={twMerge(
        'block rounded-lg transition-colors',
        isLarge ? 'p-4' : 'p-3',
        isAlert
          ? 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-900/30'
          : 'bg-primary-100 dark:bg-primary-800 hover:bg-primary-200 dark:hover:bg-primary-700',
      )}
    >
      {isLarge && (
        <p className="text-xs font-medium text-primary-600 dark:text-primary-300 uppercase tracking-wide">
          {label}
        </p>
      )}
      {isLoading ? (
        <Skeleton
          className={twMerge(
            skeletonWidth,
            isLarge ? 'h-9 my-1' : 'h-6 mb-1.5',
          )}
        />
      ) : (
        <p
          className={twMerge(
            'font-bold tabular-nums',
            isLarge ? 'text-2xl mt-1' : 'text-xl',
            isAlert
              ? 'text-red-600 dark:text-red-400'
              : 'text-primary-900 dark:text-primary-100',
          )}
        >
          {value}
        </p>
      )}
      {!isLarge && (
        <div className="mt-0.5 min-w-0">
          <p
            className={twMerge(
              'text-xs leading-tight',
              isAlert
                ? 'text-red-600 dark:text-red-400'
                : 'text-primary-600 dark:text-primary-300',
            )}
          >
            {label}
          </p>
          {description && (
            <p
              title={description}
              className="mt-0.5 truncate text-[11px] leading-tight text-primary-500 dark:text-primary-400"
            >
              {description}
            </p>
          )}
        </div>
      )}
    </Link>
  )
}

export const ACTION_LINK_CLASSNAME =
  'flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300'

interface ActionLinkProps {
  href: string
  children: React.ReactNode
  className?: string
}

export const ActionLink: React.FC<ActionLinkProps> = ({
  href,
  children,
  className,
}) => (
  <Link href={href} className={twMerge(ACTION_LINK_CLASSNAME, className)}>
    {children}
    <span className="text-[11px]">→</span>
  </Link>
)

const DEFAULT_ACCESS_BANNER_TITLE = 'Upgrade to unlock'
const DEFAULT_ACCESS_BANNER_DESCRIPTION =
  'This feature is available on paid plans. Talk to our team to learn more.'

interface AccessBannerProps {
  title?: string
  description?: string
  className?: string
  buttonVisible?: boolean
}

export const AccessBanner: React.FC<AccessBannerProps> = ({
  title,
  description,
  className,
  buttonVisible = true,
}) => {
  title = title || DEFAULT_ACCESS_BANNER_TITLE
  description = description || DEFAULT_ACCESS_BANNER_DESCRIPTION

  return (
    <div
      className={twMerge(
        'mx-3 my-3 p-3 rounded-lg',
        'bg-gradient-to-r from-white to-accent-200 dark:from-default-dark dark:to-accent-900/40',
        'border border-accent-200 dark:border-accent-700',
        className,
      )}
    >
      <p className="text-sm font-medium text-primary-800 dark:text-primary-200 mb-2">
        {title}
      </p>
      <p
        className={twMerge(
          'text-[13px] text-primary-600 dark:text-primary-400',
          buttonVisible ? 'mb-3' : 'mb-0',
        )}
      >
        {description}
      </p>
      {buttonVisible && (
        <Link
          href={DEMO_URL}
          className={twMerge(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium',
            'bg-blue-600 text-white hover:bg-blue-700',
            'transition-colors duration-75',
          )}
        >
          Book a demo
          <HiOutlineArrowRight size={12} />
        </Link>
      )}
    </div>
  )
}

const NAV_ITEMS_WITH_FLYOUT: Set<NavigationId> = new Set([
  'home',
  'search',
  'pursuits',
  'library',
  'ask-ai',
])

interface SidebarNavItemProps {
  option: NavigationOption
  canAccess: boolean
  isHovered: boolean
  badgeCount?: number
  /** Render a small unread dot (no count) — used by the Inbox item. */
  showDot?: boolean
  onHover: (optionId: string | null) => void
  onClick?: () => void
}

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  option,
  canAccess,
  isHovered,
  badgeCount,
  showDot = false,
  onHover,
  onClick,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { isLoggedIn } = useAuthInfo()
  const pathname = usePathname()

  const hoverSuppressed = useSidebarHoverSuppressed()
  const setHoverSuppressed = useSidebarSetHoverSuppressed()

  const itemIsActive = isActive(option, pathname)
  const hasFlyout = NAV_ITEMS_WITH_FLYOUT.has(option.id as NavigationId)
  const allowHoverForFlyout = isLoggedIn && !canAccess && hasFlyout

  const href =
    activeOrgId === ''
      ? '#'
      : option.isOrganizationFeature
        ? `/orgs/${activeOrgId}${option.href}`
        : option.href

  const isHighlighted = itemIsActive
  const isClickable = canAccess && activeOrgId !== ''
  const usesPlaceholderHref = href === '#'

  const handleMouseEnter = useCallback(() => {
    if (canAccess || allowHoverForFlyout) {
      onHover(hasFlyout ? option.id : null)
    }
  }, [canAccess, allowHoverForFlyout, hasFlyout, option.id, onHover])

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (allowHoverForFlyout) {
        e.preventDefault()
      } else if (isClickable) {
        onClick?.()
        // suppress the flyout panel from opening when clicking a link directly
        setHoverSuppressed(true)
      }
    },
    [allowHoverForFlyout, isClickable, onClick, setHoverSuppressed],
  )

  const handleMouseLeave = useCallback(() => {
    // if clicking on this element caused the hover to be suppressed, clear it
    if (hoverSuppressed) {
      setHoverSuppressed(false)
    }
  }, [hoverSuppressed, setHoverSuppressed])

  const iconElement = (
    <div
      className={twMerge(
        'group/icon relative flex items-center justify-center w-8 h-8 rounded-lg',
        // the professional rail is dark, so its states invert: pale blue icons
        // on navy, active in banana
        'transition-all duration-75 ease-out text-primary-700 dark:text-primary-300 professional:text-accent-100',
        isClickable
          ? isHighlighted
            ? 'bg-blue-100 dark:bg-accent-500/20 text-blue-600 dark:text-accent-400 professional:bg-banana-400/15 professional:text-banana-300'
            : isHovered
              ? 'bg-primary-200 dark:bg-primary-800 hover:scale-105 professional:bg-white/10 professional:text-accent-50'
              : 'hover:bg-primary-200 dark:hover:bg-primary-800 hover:scale-105 professional:hover:bg-white/10 professional:hover:text-accent-50'
          : 'opacity-60 cursor-not-allowed text-primary-400',
      )}
      onMouseEnter={handleMouseEnter}
    >
      <div
        className={twMerge(
          'absolute -left-[21px] top-1/2 -translate-y-1/2 w-[3px] rounded-full transition-all duration-75',
          itemIsActive
            ? 'h-6 bg-blue-600 dark:bg-accent-400 professional:bg-banana-400'
            : 'h-0 bg-transparent',
        )}
      />
      {option.icon !== null && (
        <div className="relative">
          <div
            className={twMerge(
              'transition-transform duration-75',
              isClickable && !isHighlighted && 'group-hover/icon:scale-105',
            )}
          >
            {option.activeIcon !== null && isHighlighted && isClickable ? (
              <option.activeIcon size={20} />
            ) : (
              <option.icon size={20} />
            )}
          </div>
          {badgeCount !== undefined && badgeCount > 0 ? (
            // the ring punches the badge out of the rail, so it has to be
            // painted in whatever the rail is
            <div className="absolute -top-[6px] -right-[6px] rounded-full ring-2 ring-primary-50 dark:ring-default-dark professional:ring-accent-800">
              <MiniBadge count={badgeCount} variant="danger" />
            </div>
          ) : (
            showDot && (
              <span
                aria-hidden={true}
                className="absolute -right-[1px] -top-[1px] flex h-2 w-2"
              >
                {/* Pulsing halo behind a crisp dot; halo is dropped under
                    prefers-reduced-motion so it doesn't flash. */}
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-500 opacity-75 motion-reduce:hidden professional:bg-banana-300" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600 ring-2 ring-primary-50 dark:bg-accent-500 dark:ring-default-dark professional:bg-banana-400 professional:ring-accent-800" />
              </span>
            )
          )}
        </div>
      )}
    </div>
  )

  const showTooltip = !canAccess && !hasFlyout

  const className = twMerge(
    'relative flex flex-col items-center py-1.5',
    // the blue ring all but vanishes against the navy rail, so the
    // professional theme keeps the focus ring on the one hue that is always
    // legible there
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/50 dark:focus-visible:ring-blue-400/50 professional:focus-visible:ring-banana-300/80 rounded-lg',
    !isClickable && !allowHoverForFlyout && 'pointer-events-none',
  )

  const content = (
    <>
      {showTooltip ? (
        <DemoTooltip side="right" sideOffset={8}>
          {iconElement}
        </DemoTooltip>
      ) : (
        iconElement
      )}
      <span
        className={twMerge(
          'text-xs font-medium leading-tight mt-1 select-none text-center w-[64px] break-words',
          'transition-all duration-75 tracking-tight',
          // the label sits on the rail, not on the page, so in the professional
          // theme it has to leave the warm neutral ramp entirely —
          // `primary-700` is a brown that disappears into the navy
          isHighlighted && isClickable
            ? 'text-blue-600 dark:text-accent-300 professional:text-banana-300'
            : 'text-primary-700 dark:text-primary-300 professional:text-accent-100',
        )}
      >
        {option.name}
      </span>
    </>
  )

  if (usesPlaceholderHref) {
    return (
      <a
        href="#"
        className={className}
        onMouseLeave={handleMouseLeave}
        onClick={handleLinkClick}
        aria-disabled={!isClickable && !allowHoverForFlyout}
      >
        {content}
      </a>
    )
  }

  return (
    <Link
      href={href}
      className={className}
      onMouseLeave={handleMouseLeave}
      onClick={handleLinkClick}
    >
      {content}
    </Link>
  )
}
